import * as React from 'react';

import { ImageAsDocumentMessage } from '../../components/ImageAsDocumentMessage';
import { TImageFileAsDocument } from '../../types/messageFiles';
import { formatBytes } from '../../utils/formatBytes';
import { getFileSrc } from '../../utils/getFileSrc';
import { useMessageContext } from '../../utils/messageContext';
import { ChatImageContainer } from '../ChatImageContainer';
import { ChatImageDraftContainer } from '../ChatImageDraftContainer';
import { useImageAsDocument } from '../ChatMessageContainer/hooks';

export const ImageAsDocumentMessageContainer: React.FC = () => {
  const { message, messageId, chatId } = useMessageContext();

  const { files, isOutcoming, status } = message;

  const { imageAsDocumentDraft, imageAsDocument } = useImageAsDocument(messageId, files?.imagesAsDocuments?.[0]);

  const image = React.useMemo(() => {
    if (imageAsDocumentDraft) {
      return (
        <ChatImageDraftContainer
          file={imageAsDocumentDraft.file}
          messageId={messageId}
          cancellationToken={imageAsDocumentDraft.cancellationToken}
          messageStatus={status}
        />
      );
    }

    return <ChatImageContainer image={imageAsDocument as TImageFileAsDocument} withSave />;
  }, [imageAsDocument, imageAsDocumentDraft, messageId, status]);

  const src = React.useMemo(() => {
    if (imageAsDocument?.uid) {
      return getFileSrc(imageAsDocument.uid, chatId);
    }

    return null;
  }, [chatId, imageAsDocument?.uid]);

  const name = React.useMemo(() => {
    return imageAsDocument?.name || imageAsDocumentDraft?.file?.name || '';
  }, [imageAsDocument?.name, imageAsDocumentDraft?.file?.name]);

  const size = React.useMemo(() => {
    const sizeNumber = imageAsDocument?.size || imageAsDocumentDraft?.file?.size;

    return formatBytes(sizeNumber);
  }, [imageAsDocument?.size, imageAsDocumentDraft?.file?.size]);

  if (!imageAsDocumentDraft && !imageAsDocument) {
    return null;
  }

  return <ImageAsDocumentMessage imageNode={image} name={name} size={size} src={src} isOutcoming={isOutcoming} />;
};
